import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Eye, FileSpreadsheet, FileText, Loader2, RefreshCw, Search } from 'lucide-react';
import { PedidoDetalleDialog } from './PedidoDetalleDialog';
import { ESTADOS_PEDIDO, descargarArchivo, fmtCuit, fmtFecha, fmtFechaHora, fmtNum } from './formato';

const TODOS = '__todos__';
const ICONOS = { XLSX: FileSpreadsheet, PDF: FileText };

const FiltroSelect = ({ value, onChange, placeholder, opciones, testid, width = 'w-44' }) => (
  <Select value={value || TODOS} onValueChange={(v) => onChange(v === TODOS ? '' : v)}>
    <SelectTrigger className={`${width} h-9`} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value={TODOS}>{placeholder}</SelectItem>
      {opciones.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const HistorialPedidos = ({ motivos, destacado }) => {
  const [f, setF] = useState({ q: '', estado: '', motivo: '', usuario_id: '', desde: '', hasta: '' });
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [bajando, setBajando] = useState(null);

  const cargar = useCallback(async () => {
    const params = { page, page_size: 25, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
    try { setData((await api.get('/export-movimientos/pedidos', { params })).data); } catch { setData({ items: [], total: 0 }); }
  }, [f, page]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    if (!data?.items?.some((p) => p.estado === 'GENERANDO')) return undefined;
    const t = setTimeout(cargar, 3000);
    return () => clearTimeout(t);
  }, [data, cargar]);

  const set = (k, v) => { setPage(1); setF((x) => ({ ...x, [k]: v })); };
  const bajar = async (p, fmt) => {
    setBajando(`${p.id}-${fmt}`);
    try { await descargarArchivo(api, p, fmt); cargar(); } catch { toast.error('No se pudo descargar'); } finally { setBajando(null); }
  };

  const items = data?.items || [];
  const paginas = Math.max(1, Math.ceil((data?.total || 0) / 25));
  return (
    <Card className="p-4 sm:p-5 border-slate-200 dark:border-slate-800" data-testid="expmov-historial">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && set('q', q)} onBlur={() => set('q', q)}
            placeholder="N° de pedido, CUIT, CVU, titular o referencia" className="pl-8 w-80 h-9" data-testid="expmov-hist-buscar" />
        </div>
        <FiltroSelect value={f.estado} onChange={(v) => set('estado', v)} placeholder="Todos los estados" testid="expmov-hist-estado"
          opciones={Object.entries(ESTADOS_PEDIDO).map(([value, e]) => ({ value, label: e.label }))} width="w-40" />
        <FiltroSelect value={f.motivo} onChange={(v) => set('motivo', v)} placeholder="Todos los motivos" testid="expmov-hist-motivo" opciones={motivos} />
        {data?.es_admin && (
          <FiltroSelect value={f.usuario_id} onChange={(v) => set('usuario_id', v)} placeholder="Todos los usuarios" testid="expmov-hist-usuario"
            opciones={(data.usuarios || []).map((u) => ({ value: u.id, label: u.nombre }))} width="w-48" />
        )}
        <Input type="date" value={f.desde} onChange={(e) => set('desde', e.target.value)} className="w-36 h-9" data-testid="expmov-hist-desde" />
        <Input type="date" value={f.hasta} onChange={(e) => set('hasta', e.target.value)} className="w-36 h-9" data-testid="expmov-hist-hasta" />
        <Button size="sm" variant="ghost" onClick={cargar} data-testid="expmov-hist-refrescar"><RefreshCw className="h-4 w-4" /></Button>
        <span className="ml-auto text-xs text-slate-500" data-testid="expmov-hist-total">
          {fmtNum(data?.total || 0)} pedido(s){data?.es_admin ? ' · ves los de todos los usuarios' : ' · ves solo los tuyos'}
        </span>
      </div>

      {!data ? <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : items.length === 0 ? (
        <p className="py-10 text-center text-sm text-slate-500" data-testid="expmov-hist-vacio">Todavía no hay pedidos.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="expmov-hist-tabla">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-700">
                {['N°', 'Fecha', ...(data.es_admin ? ['Usuario'] : []), 'Titular', 'Período', 'Motivo', 'Movs.', 'Estado', 'Descargas', ''].map((h) => (
                  <th key={h} className="py-2 pr-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const est = ESTADOS_PEDIDO[p.estado] || ESTADOS_PEDIDO.GENERANDO;
                return (
                  <tr key={p.id} className={`border-b border-slate-100 dark:border-slate-800 ${p.id === destacado ? 'bg-blue-50/70 dark:bg-blue-900/20' : ''}`} data-testid={`expmov-pedido-${p.numero}`}>
                    <td className="py-2 pr-3 font-mono font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">{p.numero}</td>
                    <td className="py-2 pr-3 whitespace-nowrap text-slate-600">{fmtFechaHora(p.created_at_ar)}</td>
                    {data.es_admin && <td className="py-2 pr-3 text-slate-600">{p.usuario_nombre}</td>}
                    <td className="py-2 pr-3 max-w-[190px]">
                      <div className="truncate text-slate-800 dark:text-slate-100">{p.titular || '—'}</div>
                      <div className="text-[11px] font-mono text-slate-400 whitespace-nowrap">{p.cuit ? fmtCuit(p.cuit) : p.objetivo} · {p.filtros.cvus.length} cta.</div>
                    </td>
                    <td className="py-2 pr-3 text-slate-600 text-xs leading-tight">{fmtFecha(p.filtros.desde)}<br />{fmtFecha(p.filtros.hasta)}</td>
                    <td className="py-2 pr-3 text-slate-600 max-w-[130px]">
                      <div className="truncate">{p.motivo_label || '—'}</div>
                      {p.referencia && <div className="text-[11px] text-slate-400 truncate">{p.referencia}</div>}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{p.resumen ? fmtNum(p.resumen.cantidad) : '—'}</td>
                    <td className="py-2 pr-3">
                      <Badge className={`border-0 text-[10px] ${est.cls}`} data-testid={`expmov-estado-${p.numero}`}>
                        {p.estado === 'GENERANDO' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}{est.label}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-center tabular-nums text-slate-600" data-testid={`expmov-descargas-${p.numero}`}>{p.cantidad_descargas}</td>
                    <td className="py-2 whitespace-nowrap">
                      {p.estado === 'LISTO' && (p.archivos || []).map((a) => {
                        const Icon = ICONOS[a.formato];
                        return (
                          <Button key={a.formato} size="sm" variant="ghost" className="h-8 px-2" title={`Descargar ${a.formato}`} disabled={!!bajando}
                            onClick={() => bajar(p, a.formato)} data-testid={`expmov-bajar-${a.formato}-${p.numero}`}>
                            {bajando === `${p.id}-${a.formato}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
                          </Button>
                        );
                      })}
                      <Button size="sm" variant="ghost" className="h-8 px-2" title="Ver detalle" onClick={() => setDetalle(p.id)} data-testid={`expmov-ver-${p.numero}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {paginas > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="expmov-hist-prev">Anterior</Button>
          <span className="text-slate-500">Página {page} de {paginas}</span>
          <Button size="sm" variant="outline" disabled={page >= paginas} onClick={() => setPage(page + 1)} data-testid="expmov-hist-next">Siguiente</Button>
        </div>
      )}
      <PedidoDetalleDialog pedidoId={detalle} onClose={() => setDetalle(null)} onCambio={cargar} />
    </Card>
  );
};

export default HistorialPedidos;
